import React from 'react';
import { Link } from 'react-router-dom';

import Seo from '../ui/Seo';
import Reveal from '../ui/Reveal';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import SectionHead from '../sections/SectionHead';
import Stats from '../sections/Stats';
import VideoReel from '../sections/VideoReel';
import StoryDeck from '../sections/StoryDeck';
import Bento from '../sections/Bento';
import ProcessRail from '../sections/ProcessRail';
import Faqs from '../sections/Faqs';
import Cta from '../sections/Cta';
import { contact, site } from '../data/site';

import TpiLogo from '../media/images/tpi/tpi-logo.png';
import PlantAerial from '../media/images/tpi/plant-aerial.jpg';
import Design3d from '../media/images/tpi/design-3d.jpg';
import LaserCutting from '../media/images/tpi/laser-cutting.jpg';
import CopperBusbar from '../media/images/tpi/copper-busbar.jpg';
import PowderCoating from '../media/images/tpi/powder-coating.jpg';
import SwitchgearLineups from '../media/images/tpi/switchgear-lineups.jpg';
import QualityInspection from '../media/images/tpi/quality-inspection.jpg';

/* Everything on this page about TPI comes from one of two places: TPI's
   own site, tpinge.com (the thirty years, the address, the four
   projects), or TPI's two films (the plant, its processes, and the UL
   Listed and RETIE signs on its wall). Every photograph is a frame from
   those films. Anything added here should have a source like that, or be
   confirmed with TPI first. */
const TPI = {
    name: 'TPI Ingeniería & Suministros',
    short: 'TPI',
    url: 'https://www.tpinge.com/'
};

/* Encoded with scripts/encode-video.sh and served from public/media/tpi/
   (see the README there). A clip can also be a YouTube id, and one with
   no source at all shows as "coming soon". */
const clips = [
    {
        id: 'story',
        title: 'Meet TPI',
        caption: 'The plant, the people and the process, narrated in English: from sheet steel and copper to finished switchgear.',
        src: '/media/tpi/tpi-story.mp4',
        poster: '/media/tpi/tpi-story.jpg',
        length: '2:32',
        duration: 'PT2M32S'
    },
    {
        id: 'plant-tour',
        title: 'The plant tour',
        caption: 'Three and a half minutes on the production floor, set to music: laser cutting, busbar, powder coating, assembly and inspection.',
        src: '/media/tpi/tpi-plant-tour.mp4',
        poster: '/media/tpi/tpi-plant-tour.jpg',
        length: '3:22',
        duration: 'PT3M22S'
    }
];

/* The plant in the order the films walk through it. */
const plant = [
    {
        id: 'design',
        kicker: 'Design',
        title: 'Modelled before metal is cut',
        text: 'Every lineup starts as a 3D model: sections, busbar runs, breakers and clearances laid out and checked against the drawings for your building before anything goes to the floor.',
        imgSrc: Design3d,
        alt: 'A TPI engineer reviewing a 3D model of a switchgear lineup',
        focus: '35%',
        points: ['3D models', 'Front elevations', 'Coordinated with our drawings']
    },
    {
        id: 'fabrication',
        kicker: 'Fabrication',
        title: 'Cut, punched and formed in-house',
        text: 'Fiber laser cutting, CNC turret punching and press-brake forming turn sheet steel into the frames, doors and mounting plates of every enclosure, all under one roof.',
        imgSrc: LaserCutting,
        alt: 'A fiber laser cutting sheet steel at the TPI plant',
        focus: '40%',
        points: ['Fiber laser', 'CNC punching', 'Press brakes']
    },
    {
        id: 'busbar',
        kicker: 'Busbar',
        title: 'Copper, processed on CNC',
        text: 'Copper busbar is cut, punched and bent on dedicated CNC busbar machines, so every bar and every bolt hole lands exactly where the model puts it.',
        imgSrc: CopperBusbar,
        alt: 'Punched copper busbars at the TPI plant',
        focus: '50%',
        points: ['CNC busbar machines', 'Hydraulic bending', 'Copper distribution']
    },
    {
        id: 'finish',
        kicker: 'Finish',
        title: 'Powder coated to last',
        text: 'Enclosures are finished with electrostatic powder coating: a hard, even coat that resists corrosion and wear, which is exactly what equipment needs in South Florida’s salt air.',
        imgSrc: PowderCoating,
        alt: 'Electrostatic powder coating in the TPI paint booth',
        focus: '30%',
        points: ['Electrostatic powder coat', 'Corrosion resistant', 'Wear resistant']
    },
    {
        id: 'assembly',
        kicker: 'Assembly',
        title: 'Built as complete lineups',
        text: 'Switchboards, switchgear and motor control centers are assembled and wired on a production floor laid out for full lineups, so a lineup is put together as a whole at the plant.',
        imgSrc: SwitchgearLineups,
        alt: 'Switchboard and switchgear lineups on the TPI production floor',
        focus: '40%',
        points: ['Switchboards', 'Switchgear', 'Motor control centers', 'Control wiring']
    },
    {
        id: 'quality',
        kicker: 'Quality',
        title: 'Inspected before it ships',
        text: 'Each lineup is inspected on the floor, then wrapped and crated for the trip to site, where our crews take it from there.',
        imgSrc: QualityInspection,
        alt: 'A TPI inspector checking a motor control center lineup',
        focus: '45%',
        points: ['Floor inspection', 'Wrapped and crated', 'Installed by us']
    }
];

const advantages = [
    {
        title: 'Built for your project',
        body: 'Switchboards, panels and enclosures designed around your loads, your layout and your electrical room, rather than the closest size in a catalogue.'
    },
    {
        title: 'Coordinated before it is built',
        body: 'TPI models every lineup in 3D, so dimensions, clearances and connection points are checked against the building before any metal is cut.'
    },
    {
        title: 'One team, one contact',
        body: 'The engineers who design the equipment work directly with the crew that installs it. You deal with one company from the first drawing to the final inspection: us.'
    },
    {
        title: 'Certified manufacturing',
        body: 'TPI is a UL Listed manufacturer and certified under RETIE, Colombia’s electrical regulation, and we take care of the permit and the inspection here in Florida.'
    },
    {
        title: 'A finish made for the coast',
        body: 'Enclosures are finished with electrostatic powder coating, which resists the corrosion and wear that salt air brings to South Florida equipment.'
    },
    {
        title: 'Precision you can see',
        body: 'Laser cutting, CNC punching and CNC busbar machines put every cut-out, hole and bar exactly where the model says, so the equipment goes in cleanly and stays easy to service.'
    }
];

const steps = [
    {
        step: '01',
        title: 'Designed together',
        body: 'We start from your loads, your schedule and your electrical room. TPI’s engineers model the equipment against our drawings, so it is coordinated before fabrication begins.'
    },
    {
        step: '02',
        title: 'Built at TPI',
        body: 'Cut, punched, powder coated, assembled and wired in TPI’s own plant, then inspected and crated, by a team that has been doing it for more than 30 years.'
    },
    {
        step: '03',
        title: 'Installed by us',
        body: 'Our crews set, terminate and commission the equipment, take it through inspection, and stay your single point of contact for service afterwards.'
    }
];

const record = [
    {
        name: 'Sortin 3, Tecnoglass',
        place: 'Barranquilla, Atlántico',
        text: 'Electrical installation and low-voltage power panel assembly for the 630 kVA substation serving an automated warehouse.'
    },
    {
        name: '0.8 MVA substation, AB Marine Group',
        place: 'Barranquilla, Atlántico',
        text: 'Transformation and distribution, with the low-voltage power panels for the substation designed, built and installed by TPI.'
    },
    {
        name: 'Power panels, Extractora El Pajuil',
        place: 'Maní, Casanare',
        text: 'Motor control centers and power distribution panels, with a SCADA system for the MCCs and automation of the plant’s sterilization process.'
    },
    {
        name: '500 kVA basement substation, Unión Vital',
        place: 'Barranquilla, Atlántico',
        text: 'Transformation and distribution, with low-voltage power panels designed, built and installed for the substation.'
    }
];

const faqs = [
    {
        question: 'Do I still hire Smart Electric Solutions?',
        answer: 'Yes. Your contract, your schedule and your point of contact stay with us. We bring TPI in where custom-built equipment is the better answer for your project, and we manage that work as part of ours.'
    },
    {
        question: 'What kind of projects is this for?',
        answer: 'Commercial, industrial, mixed-use and larger residential buildings, where the electrical room is built around switchboards, switchgear, motor control centers or special-purpose enclosures. For a typical home panel upgrade, standard equipment is still the right call, and we will tell you so.'
    },
    {
        question: 'Is the equipment built to US standards?',
        answer: 'TPI is a UL Listed manufacturer, and also certified under RETIE, Colombia’s electrical regulation. As your electrical contractor, we pull the permit and take the installation through inspection with the local authority, the same as on any other job.'
    },
    {
        question: 'Where is TPI based?',
        answer: 'In Barranquilla, Colombia, where TPI has designed, built and installed electrical equipment for more than 30 years.'
    },
    {
        question: 'Who do I call if something needs service?',
        answer: 'Us. We install and commission the equipment, so we are your first call for service, and we coordinate with TPI on anything that needs the manufacturer.'
    },
    {
        question: 'How do I find out if it fits my project?',
        answer: `Call ${contact.phone} or request a consultation. Tell us what you are building and we will tell you where equipment built by TPI makes sense.`
    }
];

export default function Tpi() {
    return (
        <>
            <Seo
                title="TPI Partnership"
                description="Smart Electric Solutions has partnered with TPI Ingeniería & Suministros, a UL Listed manufacturer of switchboards, switchgear and motor control centers, to engineer, build and install electrical equipment for projects across South Florida."
                keywords="TPI Ingeniería, TPI Ingenieria y Suministros, custom switchboards, switchgear, motor control centers, copper busbar, UL Listed panel manufacturer, electrical contractor South Florida"
                path="/tpi"
                schema={[
                    {
                        '@type': 'WebPage',
                        name: 'TPI Partnership',
                        description: 'Smart Electric Solutions and TPI Ingeniería & Suministros: custom-built electrical equipment, installed by one contractor.',
                        url: `${site.origin}/tpi`,
                        about: [
                            { '@type': 'Organization', name: site.name, url: site.origin },
                            {
                                '@type': 'Organization',
                                name: TPI.name,
                                url: TPI.url,
                                address: { '@type': 'PostalAddress', addressLocality: 'Barranquilla', addressCountry: 'CO' }
                            }
                        ]
                    },
                    ...clips.map(clip => ({
                        '@type': 'VideoObject',
                        name: `${clip.title}: ${TPI.name}`,
                        description: clip.caption,
                        thumbnailUrl: `${site.origin}${clip.poster}`,
                        contentUrl: `${site.origin}${clip.src}`,
                        uploadDate: '2026-10-29',
                        duration: clip.duration
                    }))
                ]}
            />

            <PageHero
                eyebrow="Partnership"
                title="Built by TPI. Installed by us."
                lead={`Smart Electric Solutions has partnered with ${TPI.name}, who have designed and built switchboards and switchgear for more than 30 years. For you, it means the equipment at the heart of your building can be engineered for your project and installed by the team that planned it.`}
                backdrop={PlantAerial}
                focus="55% 60%"
                crumbs={[{ label: 'TPI Partnership' }]}
                actions={
                    <>
                        <a className="btn btn--accent" href="#videos">
                            Watch the films
                            <span className="btn__arrow" aria-hidden="true">↓</span>
                        </a>
                        <Link className="btn btn--ghost" to="/#contact">
                            Talk to us about a project
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                    </>
                }
            >
                <Reveal delay={0.38} amount={0.1} className="lockup">
                    <img className="lockup__ses" src={site.logo} alt={site.name} width="1920" height="1080" />
                    <span className="lockup__x" aria-hidden="true">×</span>
                    <img className="lockup__tpi" src={TpiLogo} alt={TPI.name} width="800" height="263" />
                </Reveal>
            </PageHero>

            <TradeTicker />

            <section className="section">
                <div className="shell">
                    <SectionHead
                        eyebrow="The partnership"
                        title="A manufacturer and a contractor, on one team."
                    />

                    <Reveal className="prose" y={24} delay={0.1}>
                        <p>{TPI.name} is an electrical engineering and manufacturing company in Barranquilla, Colombia, with more than 30 years in the market. In their own plant they design, fabricate and assemble switchboards, switchgear and motor control centers, down to the enclosures and the copper busbar inside them.</p>
                        <p>We install, commission and maintain electrical systems across South Florida. Working together, the equipment in your electrical room and the crew that installs it are planned as one job from the first drawing, and you still have one company to call: us. Learn more about TPI at{' '}
                            <a href={TPI.url} target="_blank" rel="noopener noreferrer">tpinge.com</a>.
                        </p>
                    </Reveal>

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <Stats items={[
                            { value: '30+', label: 'Years in the market' },
                            { value: 'UL', label: 'Listed manufacturer' },
                            { value: 'RETIE', label: 'Certified in Colombia' },
                            { value: 'CNC', label: 'Laser, punch and busbar' }
                        ]} />
                    </div>
                </div>
            </section>

            <section className="section section--ruled" id="videos">
                <div className="shell">
                    <SectionHead
                        eyebrow="Watch"
                        title="See how it is built."
                        lead="Two films from inside TPI’s plant: one narrated, one a plant tour set to music."
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <VideoReel clips={clips} />
                    </div>
                </div>
            </section>

            <section className="section section--ruled" id="plant">
                <div className="shell">
                    <SectionHead
                        eyebrow="Inside the plant"
                        title="From sheet steel and copper to switchgear."
                        lead="The same walk the films take, one step at a time: everything that happens to your equipment before it reaches the site."
                    />
                </div>

                <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                    <StoryDeck items={plant} />
                </div>
            </section>

            <Bento
                eyebrow="Why it matters"
                title="What the partnership gives you."
                lead="Switchboards and panels are usually ordered from a catalogue and fitted to the room afterwards. With TPI, they can be engineered for your room from the start, by people already working with the crew that will install them."
                items={advantages}
                cols={3}
                outro="Not every job needs custom-built equipment. When yours does, we will say so, and TPI will build it."
            />

            <ProcessRail eyebrow="How it works" title="From the drawing to the switch-on." items={steps} />

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead
                        eyebrow="Track record"
                        title="Recent work by TPI."
                        lead="A selection of substations, power panels and control systems from TPI’s portfolio in Colombia."
                    />

                    <ul className="roster" style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        {record.map((item, i) => (
                            <Reveal as="li" key={item.name} delay={(i % 2) * 0.08} y={22}>
                                <h3>{item.name}</h3>
                                <p className="leader-role">{item.place}</p>
                                <p>{item.text}</p>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="section section--ruled" id="data-centers">
                <div className="shell">
                    <SectionHead
                        eyebrow="Data centers"
                        title="Monitoring for the power inside a data center."
                        lead="Switchgear and busbar are what a data center runs on, so SmartDC monitors them: AC power quality, the UPS DC bus, connection temperatures, insulation and backup capacity, all in one read-only system."
                    />

                    <div style={{ marginTop: 'clamp(1.75rem, 4vw, 2.5rem)' }}>
                        <Reveal delay={0.16}>
                            <div className="page-hero__actions">
                                <Link className="btn btn--accent" to="/data-center-monitoring">
                                    See how it works
                                    <span className="btn__arrow" aria-hidden="true">→</span>
                                </Link>
                                <Link className="btn btn--ghost" to="/data-center-monitoring/demo">
                                    Open the demo
                                    <span className="btn__arrow" aria-hidden="true">→</span>
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Faqs items={faqs} />

            <Cta
                eyebrow="Start a project"
                title="Engineered, built and installed."
                lead="Tell us what you are building anywhere in South Florida. We will tell you where equipment built by TPI makes sense, and plan it with the rest of the electrical package."
            />
        </>
    );
}
